import React, { useEffect, useMemo, useState } from 'react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import type { AdminUserSummary } from 'types';
import PerformanceAccountFilter from './PerformanceAccountFilter';
import {
  adminPerformance,
  type PerformanceFilters,
  type PerformanceGroup,
  type PerformanceReport,
  type PerformanceUnit,
} from 'services/api/adminPerformanceClient';

const metricNames: Record<string, string> = {
  page_load: 'Initial page load',
  route_settle: 'Page navigation (estimate)',
  route_timeout: 'Navigation unfinished at 15 seconds',
  request: 'API request',
  script: 'Script load',
  stylesheet: 'Stylesheet load',
  image: 'Image load',
  font: 'Font load',
  LCP: 'Main content visible (LCP)',
  FCP: 'First content visible (FCP)',
  CLS: 'Layout shifts (CLS score)',
  FID: 'First interaction delay (FID)',
  INP: 'Interaction responsiveness (INP)',
  TTFB: 'First byte (TTFB)',
};
const metricName = (name: string) => metricNames[name] || name;
const measurement = (value: number | null, unit: PerformanceUnit = 'ms') => {
  if (value === null || !Number.isFinite(value)) return 'Unavailable';
  return unit === 'score' ? value.toFixed(3) : `${Math.round(value).toLocaleString()} ms`;
};
const rowKey = (row: PerformanceGroup) =>
  JSON.stringify([row.route, row.endpoint, row.kind, row.name, row.device]);

const average = (groups: PerformanceGroup[]) => {
  const count = groups.reduce((sum, group) => sum + group.count, 0);
  return count ? groups.reduce((sum, group) => sum + group.avg_ms * group.count, 0) / count : null;
};

function PerformanceOverview({ groups }: { groups: PerformanceGroup[] }) {
  const initialLoads = groups.filter((group) => group.kind === 'navigation' && group.name === 'page_load');
  const navigations = groups.filter((group) => group.kind === 'route' && group.name === 'route_settle');
  const requests = groups.filter((group) => group.kind === 'api');
  // Server sample counts are not exposed in the report, so do not combine group
  // means using total request counts (some requests have no server timing).
  const slowestServer = requests.filter((group) => group.server_avg_ms !== null)
    .sort((a, b) => (b.server_avg_ms ?? 0) - (a.server_avg_ms ?? 0))[0];
  const slowestPages = [...initialLoads, ...navigations].sort((a, b) => b.avg_ms - a.avg_ms).slice(0, 5);
  const summaries = [
    { label: 'Initial page load average', value: average(initialLoads) },
    { label: 'Page navigation average (estimate)', value: average(navigations) },
    { label: 'API response average', value: average(requests) },
    { label: 'Slowest server average', value: slowestServer?.server_avg_ms ?? null },
  ];
  return <section aria-label="Performance overview" className="space-y-5">
    <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {summaries.map(({ label, value }) => <div key={label} className="rounded-xl border border-input p-4">
        <dt className="text-sm text-muted-foreground">{label}</dt>
        <dd className="mt-2 text-2xl font-semibold">{measurement(value)}</dd>
        {label === 'Slowest server average' && slowestServer && <dd className="mt-1 break-all text-xs text-muted-foreground">{slowestServer.endpoint} · {slowestServer.device}<span className="block">Page: {slowestServer.route}</span></dd>}
      </div>)}
    </dl>
    <p className="text-xs text-muted-foreground">Averages use the samples in this report. Server timing covers Django processing; other delays can involve hosting, the network, or the device.</p>
    <div className="space-y-2">
      <h3 className="font-semibold">Slowest page measurements</h3>
      <p className="text-sm text-muted-foreground">Up to five page and device measurements ranked by average duration. Initial loads and navigation estimates are shown separately.</p>
      {!slowestPages.length ? <p className="text-sm text-muted-foreground">No completed page loads or navigations for these filters.</p> : <Table aria-label="Slowest page measurements">
        <TableHeader><TableRow><TableHead>Page</TableHead><TableHead>Measurement</TableHead><TableHead>Device</TableHead><TableHead>Samples</TableHead><TableHead>Average</TableHead></TableRow></TableHeader>
        <TableBody>{slowestPages.map((group) => <TableRow key={rowKey(group)}><TableCell className="break-all">{group.route}</TableCell><TableCell>{metricName(group.name)}</TableCell><TableCell>{group.device}</TableCell><TableCell>{group.count.toLocaleString()}</TableCell><TableCell className="whitespace-nowrap">{measurement(group.avg_ms)}</TableCell></TableRow>)}</TableBody>
      </Table>}
    </div>
  </section>;
}

function MetricTable({ groups, api = false }: { groups: PerformanceGroup[]; api?: boolean }) {
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [groups]);
  if (!groups.length) return <p className="text-sm text-muted-foreground">No measurements for these filters.</p>;
  return (
    <>
    <Table aria-label={api ? 'API response timing' : 'Page performance'}>
      <TableHeader>
        <TableRow>
          <TableHead>Page</TableHead>
          <TableHead>{api ? 'Endpoint' : 'Measurement'}</TableHead>
          <TableHead>Device</TableHead>
          <TableHead>Samples</TableHead>
          <TableHead>Average</TableHead>
          <TableHead>95th percentile</TableHead>
          {api && <TableHead>Server average</TableHead>}
          {api && <TableHead>Download average</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.slice(page * 100, (page + 1) * 100).map((group) => (
          <TableRow key={rowKey(group)}>
            <TableCell className="break-all">{group.route}</TableCell>
            <TableCell className="break-all">{api ? group.endpoint : metricName(group.name)}</TableCell>
            <TableCell>{group.device}</TableCell>
            <TableCell>{group.count.toLocaleString()}</TableCell>
            <TableCell className="whitespace-nowrap">{measurement(group.avg_ms, group.unit)}</TableCell>
            <TableCell className="whitespace-nowrap">{measurement(group.p95_ms, group.unit)}</TableCell>
            {api && <TableCell className="whitespace-nowrap">{measurement(group.server_avg_ms)}</TableCell>}
            {api && <TableCell className="whitespace-nowrap">{measurement(group.transfer_avg_ms)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {groups.length > 100 && <div className="flex items-center gap-3 text-sm">
      <Button type="button" size="auto" variant="secondary" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>Previous {api ? 'API' : 'page'} metrics</Button>
      <span>Page {page + 1} of {Math.ceil(groups.length / 100)}</span>
      <Button type="button" size="auto" variant="secondary" disabled={(page + 1) * 100 >= groups.length} onClick={() => setPage((value) => value + 1)}>Next {api ? 'API' : 'page'} metrics</Button>
    </div>}
    </>
  );
}

const PerformanceMetricsCard: React.FC = () => {
  const [days, setDays] = useState('7');
  const [account, setAccount] = useState<AdminUserSummary | null>(null);
  const [accountQuery, setAccountQuery] = useState('');
  const [route, setRoute] = useState('');
  const [knownRoutes, setKnownRoutes] = useState<string[]>([]);
  const [view, setView] = useState<'overview' | 'pages' | 'api'>('overview');
  const [filters, setFilters] = useState<PerformanceFilters>({ days: 7 });
  const [refresh, setRefresh] = useState(0);
  const [report, setReport] = useState<PerformanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const pageGroups = useMemo(() => report?.groups.filter((group) => group.kind !== 'api') || [], [report]);
  const apiGroups = useMemo(() => report?.groups.filter((group) => group.kind === 'api') || [], [report]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setHistoryPage(0);
    setHistoryOpen(false);
    setRecentOpen(false);
    adminPerformance.get(filters)
      .then(({ data }) => {
        if (!active) return;
        setReport(data);
        setKnownRoutes((current) => Array.from(new Set([...current, ...data.groups.map((group) => group.route), ...data.daily.map((day) => day.route)])).sort());
      })
      .catch(() => { if (active) setError('Unable to load performance measurements. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, refresh]);

  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (accountQuery.trim() && !account) return;
    setFilters({
      days: Number(days),
      ...(account ? { user_id: Number(account.id) } : {}),
      ...(route.trim() ? { route: route.trim() } : {}),
    });
  };

  return (
    <Card data-testid="performance-metrics-card" maxWidth="none" className="min-w-0">
      <CardHeader><CardTitle>App Performance</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Page load and API timings from users&apos; browsers, linked to their accounts.
          Detailed measurements last 30 days; daily summaries last one year.
        </p>
        <form onSubmit={applyFilters} className="flex flex-wrap items-end gap-3">
          <label className="space-y-2 text-sm">
            <span className="block">Performance window</span>
            <select value={days} onChange={(event) => setDays(event.target.value)} className="rounded-xl border border-input bg-background px-3 py-2">
              {[7, 30, 90, 365].map((value) => <option key={value} value={value}>Last {value} days</option>)}
            </select>
          </label>
          <PerformanceAccountFilter account={account} query={accountQuery}
            onQueryChange={(value) => { setAccountQuery(value); setAccount(null); }}
            onSelect={(value) => { setAccount(value); setAccountQuery(''); }} />
          <div className="min-w-0 flex-1 space-y-2 text-sm">
            <label className="block space-y-2">
              <span className="block">Page</span>
              <select value={route} onChange={(event) => setRoute(event.target.value)} className="w-full rounded-xl border border-input bg-background px-3 py-2">
                <option value="">All pages</option>
                {Array.from(new Set([...knownRoutes, ...(route ? [route] : [])])).sort().map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </label>
            <details className="space-y-2">
              <summary className="cursor-pointer text-xs text-muted-foreground">Page not listed?</summary>
              <label className="block space-y-2">
                <span className="block">Other page route</span>
                <Input value={route} onChange={(event) => setRoute(event.target.value)} placeholder="/flows/:flowName" />
              </label>
              <p className="text-xs text-muted-foreground">Enter a page pattern if it is missing from this report, such as /flows/:flowName.</p>
            </details>
          </div>
          <Button type="submit" size="auto" disabled={loading || Boolean(accountQuery.trim() && !account)}>Apply filters</Button>
          <Button type="button" size="auto" variant="secondary" disabled={loading} onClick={() => setRefresh((value) => value + 1)}>Refresh performance</Button>
        </form>
        <p className="text-xs text-muted-foreground">
          Defaults to all accounts and pages. Account filtering is limited to the last 30 days. The page list includes pages found in loaded reports.
        </p>
        {loading && <p role="status">Loading performance measurements...</p>}
        {!loading && error && <p role="alert">{error}</p>}
        {!loading && !error && report && (
          <div className="space-y-6">
            <p className="text-sm">Showing {report.effective_days} days{report.scope === 'account_details' ? ' of account detail' : ' across all accounts'}.</p>
            {report.truncated && <p role="status" className="text-sm text-muted-foreground">This report reached its row limit. Narrow the page, account, or time filters to see more complete results.</p>}
            <div role="group" aria-label="Performance view" className="flex flex-wrap gap-2">
              {(['overview', 'pages', 'api'] as const).map((value) => <Button key={value} type="button" size="auto" variant={view === value ? 'default' : 'secondary'} aria-pressed={view === value} onClick={() => setView(value)}>{value === 'overview' ? 'Overview' : value === 'pages' ? 'Page Loads' : 'API Requests'}</Button>)}
            </div>
            {view === 'overview' && <PerformanceOverview groups={report.groups} />}
            {view === 'pages' && <div className="space-y-3">
              <h3 className="font-semibold">Page performance</h3>
              <p className="text-sm text-muted-foreground">
                The 95th percentile is the time 95% of samples finish within. It is unavailable when the window includes historical summaries.
                Page navigation is a settling estimate, not a guarantee that every screen is ready.
                Unfinished navigations are counted separately at the 15-second observation limit. CLS measures layout movement, not time.
                First-party resource timings help diagnose downloads; filenames are not collected.
              </p>
              <MetricTable groups={pageGroups} />
            </div>}
            {view === 'api' && <div className="space-y-3">
              <h3 className="font-semibold">API response timing</h3>
              <p className="text-sm text-muted-foreground">
                Server time measures work inside Django. Download time measures response transfer when available.
                Remaining delay may include hosting, network transit, or the device; it is not proof of a slow user connection.
                Missing timing is shown as unavailable.
              </p>
              <MetricTable groups={apiGroups} api />
            </div>}
            <details className="space-y-3" onToggle={(event) => setHistoryOpen(event.currentTarget.open)}>
              <summary className="cursor-pointer font-semibold">Daily history ({report.daily.length} rows)</summary>
              {historyOpen && (!report.daily.length ? <p className="text-sm text-muted-foreground">No daily history for these filters.</p> : (
                <>
                <div className="max-h-96 overflow-auto">
                  <Table aria-label="Daily performance history">
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead>Page / endpoint</TableHead><TableHead>Measurement</TableHead>
                      <TableHead>Device</TableHead><TableHead>Samples</TableHead><TableHead>Average</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{report.daily.slice(historyPage * 50, (historyPage + 1) * 50).map((day, index) => (
                      <TableRow key={`${day.date}-${index}`}>
                        <TableCell className="whitespace-nowrap">{day.date}</TableCell>
                        <TableCell className="break-all">{day.route}{day.endpoint && <span className="block text-muted-foreground">{day.endpoint}</span>}</TableCell>
                        <TableCell>{metricName(day.name)}</TableCell><TableCell>{day.device}</TableCell>
                        <TableCell>{day.count.toLocaleString()}</TableCell><TableCell className="whitespace-nowrap">{measurement(day.avg_ms, day.unit)}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
                {report.daily.length > 50 && <div className="flex items-center gap-3 text-sm">
                  <Button type="button" size="auto" variant="secondary" disabled={historyPage === 0} onClick={() => setHistoryPage((value) => value - 1)}>Previous history</Button>
                  <span>Page {historyPage + 1} of {Math.ceil(report.daily.length / 50)}</span>
                  <Button type="button" size="auto" variant="secondary" disabled={(historyPage + 1) * 50 >= report.daily.length} onClick={() => setHistoryPage((value) => value + 1)}>Next history</Button>
                </div>}
                </>
              ))}
            </details>
            <details className="space-y-3" onToggle={(event) => setRecentOpen(event.currentTarget.open)}>
              <summary className="cursor-pointer font-semibold">Recent account measurements ({report.recent.length})</summary>
              <p className="text-sm text-muted-foreground">Recent retained samples for troubleshooting. Measurements are collected from signed-in accounts.</p>
              {recentOpen && (!report.recent.length ? <p className="text-sm text-muted-foreground">No recent measurements for these filters.</p> : (
                <div className="max-h-96 overflow-auto">
                  <Table aria-label="Recent account performance">
                    <TableHeader><TableRow>
                      <TableHead>Account</TableHead><TableHead>When</TableHead><TableHead>Page / endpoint</TableHead>
                      <TableHead>Measurement</TableHead><TableHead>Device</TableHead><TableHead>Value</TableHead>
                      <TableHead>Server</TableHead><TableHead>First byte</TableHead><TableHead>Download</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>{report.recent.map((sample) => (
                      <TableRow key={sample.id}>
                        <TableCell>{sample.user_id ? <><span className="block break-all">{sample.username || 'Account'}</span><span className="text-muted-foreground">ID {sample.user_id}</span></> : 'Account unavailable'}</TableCell>
                        <TableCell className="whitespace-nowrap">{new Date(sample.created_at).toLocaleString()}</TableCell>
                        <TableCell className="break-all">{sample.route}{sample.endpoint && <span className="block text-muted-foreground">{sample.endpoint}</span>}</TableCell>
                        <TableCell>{metricName(sample.name)}</TableCell><TableCell>{sample.device}</TableCell>
                        <TableCell className="whitespace-nowrap">{measurement(sample.value, sample.name === 'CLS' ? 'score' : 'ms')}</TableCell>
                        <TableCell className="whitespace-nowrap">{measurement(sample.server_ms)}</TableCell>
                        <TableCell className="whitespace-nowrap">{measurement(sample.ttfb_ms)}</TableCell>
                        <TableCell className="whitespace-nowrap">{measurement(sample.transfer_ms)}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </div>
              ))}
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PerformanceMetricsCard;
